import { useCallback, useEffect, useMemo, useState } from "react";
import { debounce } from "lodash";
import { Column } from "../../system-design/Table/DSBaseTable/components/DSBaseTable";
import useApprovedCollectionsInfiniteQuery, {
  CollectionStatus,
} from "./usePublicCollectionsInfiniteQuery";
import {
  MarketplaceStatus,
  PopulatedApprovedCollection,
} from "../../../api/collection";
import { useSearchParams } from "react-router-dom";

type TriState = "ALL" | "TRUE" | "FALSE";

interface CollectionsFilters {
  search?: string;
  status?: CollectionStatus;
  marketplace?: MarketplaceStatus[];
  licensing?: TriState;
  hasEta?: boolean;
  sort?: string; // "field:direction"
}

const MARKETPLACE_PARAM_SEPARATOR = ",";

const VALID_MARKETPLACE_STATUSES = Object.values(MarketplaceStatus);

/**
 * `?marketplace=true,pending` -> `[True, Pending]`.
 *
 * Unknown values are dropped rather than passed through: the param is
 * user-editable, and an unrecognised status would reach the backend's
 * `$in` unchanged and silently return nothing.
 *
 * Returns undefined for an absent or fully-invalid param so it can fall back
 * to the caller's initial value, and distinguishes that from an explicit empty
 * selection (which means "no marketplace constraint").
 */
const parseMarketplaceParam = (
  raw: string | null,
): MarketplaceStatus[] | undefined => {
  if (raw === null) return undefined;
  const parsed = raw
    .split(MARKETPLACE_PARAM_SEPARATOR)
    .map((part) => part.trim())
    .filter((part): part is MarketplaceStatus =>
      VALID_MARKETPLACE_STATUSES.includes(part as MarketplaceStatus),
    );
  return parsed.length ? parsed : undefined;
};

const useCollectionsList = (
  limit: number = 10,
  initialMarketplaceFilter?: MarketplaceStatus[],
) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const searchParam = searchParams.get("search") ?? "";
  const statusParam = searchParams.get("status") as
    | CollectionStatus
    | undefined;
  const marketplaceParam = parseMarketplaceParam(
    searchParams.get("marketplace"),
  );
  const licensingParam = (searchParams.get("licensing") as TriState) ?? "ALL";
  const hasEtaParam = searchParams.get("hasEta") === "1";

  const [sort, setSort] = useState<Record<string, 1 | -1>>({});
  const marketplaceFilter = initialMarketplaceFilter ?? marketplaceParam;

  const writeFilters = useCallback(
    (updates: CollectionsFilters) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);

        if (updates.search !== undefined) {
          if (!updates.search) params.delete("search");
          else params.set("search", updates.search);
        }

        if (updates.status !== undefined) {
          params.set("status", updates.status);
          if (updates.status !== "approved") {
            params.delete("marketplace");
          }
        }

        if (updates.marketplace !== undefined) {
          if (!updates.marketplace.length) params.delete("marketplace");
          else
            params.set(
              "marketplace",
              updates.marketplace.join(MARKETPLACE_PARAM_SEPARATOR),
            );
        }

        if (updates.licensing !== undefined) {
          params.set("licensing", updates.licensing);
        }

        // Absent rather than "0" when off, so an unchecked box leaves no trace
        // in the url — same rule as an empty marketplace selection.
        if (updates.hasEta !== undefined) {
          if (updates.hasEta) params.set("hasEta", "1");
          else params.delete("hasEta");
        }

        if (updates.sort !== undefined) {
          if (!updates.sort) params.delete("sort");
          else params.set("sort", updates.sort);
        }

        return params;
      });
    },
    [setSearchParams],
  );

  const debouncedWriteFilters = useMemo(
    () => debounce(writeFilters, 500),
    [writeFilters],
  );

  useEffect(
    () => () => debouncedWriteFilters.cancel(),
    [debouncedWriteFilters],
  );

  const updateFilters = useCallback(
    (updates: CollectionsFilters) => {
      if (updates.search !== undefined) debouncedWriteFilters(updates);
      else writeFilters(updates);
    },
    [writeFilters, debouncedWriteFilters],
  );

  const handleStatusChange = useCallback(
    (status: CollectionStatus) => {
      updateFilters({ status });
    },
    [updateFilters],
  );

  const filter = useMemo(() => {
    const f: Record<string, boolean | MarketplaceStatus[]> = {};
    if (statusParam === "draft") {
      f.draft = true;
    } else if (marketplaceFilter?.length) {
      f.marketplace = marketplaceFilter;
    }
    if (licensingParam === "TRUE") f.licensing = true;
    if (licensingParam === "FALSE") f.licensing = false;
    if (hasEtaParam) f.hasEta = true;
    return f;
  }, [statusParam, marketplaceFilter, licensingParam, hasEtaParam]);

  const { data, fetchNextPage, ...rest } = useApprovedCollectionsInfiniteQuery(
    searchParam,
    limit,
    true,
    sort,
    filter,
    statusParam,
  );

  const handleTextSearch = useCallback(
    (search: string) => {
      updateFilters({ search });
    },
    [updateFilters],
  );

  const handleMarketplaceFilterChange = useCallback(
    (value: MarketplaceStatus[]) => {
      updateFilters({ marketplace: value });
    },
    [updateFilters],
  );

  const handleLicensingFilterChange = useCallback(
    (value: TriState) => {
      updateFilters({ licensing: value });
    },
    [updateFilters],
  );

  const handleHasEtaFilterChange = useCallback(
    (value: boolean) => {
      updateFilters({ hasEta: value });
    },
    [updateFilters],
  );

  const handleSort = (field: string) => {
    setSort((prevSort) => {
      const currentSortValue = prevSort[field];
      if (currentSortValue === 1) {
        return { [field]: -1 };
      } else if (currentSortValue === -1) {
        return {};
      } else {
        return { [field]: 1 };
      }
    });
  };

  const columns: Column<PopulatedApprovedCollection>[] = [
    { field: "name", label: "Name", sortable: true },
    { field: "coverUrl", label: "Cover" },
    { field: "creator", label: "Creator", sortable: true },
    // The API sorts this by scheduled publish date, unscheduled ones first.
    { field: "marketplace", label: "Marketplace", sortable: true },
    { field: "productPageMetadata", label: "Plan" },
    { field: "totalPurchases", label: "Total Purchases", sortable: true },
    {
      field: "marketplace",
      sortField: "marketplace.publishedAt",
      label: "Published Date",
      sortable: true,
    },
    { field: "createdAt", label: "Created At", sortable: true },
  ];

  return {
    data,
    fetchNextPage,
    ...rest,
    handleTextSearch,
    handleSort,
    sort,
    columns,
    statusFilter: statusParam,
    setStatusFilter: handleStatusChange,
    marketplaceFilter: marketplaceParam,
    setMarketplaceFilter: handleMarketplaceFilterChange,
    licensingFilter: licensingParam,
    hasEtaFilter: hasEtaParam,
    setHasEtaFilter: handleHasEtaFilterChange,
    setLicensingFilter: handleLicensingFilterChange,
    textSearch: searchParam,
  };
};

export default useCollectionsList;
